import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import './OverviewTab.css';

/**
 * ISOs Tab Component
 * Displays ISO table with Reports page styling
 */
export function FIsTab({ fiMetrics }) {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(value || 0);
  }

  function handleViewDashboard(fiId) {
    navigate(`/fis/${fiId}/dashboard`);
  }

  function handleViewTransactions(fiId) {
    navigate(`/solventpay/transactions?fi_id=${fiId}`);
  }

  const filteredFIs = fiMetrics.filter(fi => {
    if (!searchTerm) return true;
    const lowerSearch = searchTerm.toLowerCase();
    const fiName = (fi.fi_name || '').toLowerCase();
    const fiId = String(fi.terra_fi_id || '');
    return fiName.includes(lowerSearch) || fiId.includes(lowerSearch);
  });

  // Status Chip Component
  // invertColors: for costs/slippage, positive values are bad (red), negative are good (green)
  function StatusChip({ value, showSign = false, invertColors = false }) {
    const isPositive = value >= 0;
    const isZero = Math.abs(value) < 0.01;
    let chipClass;
    if (isZero) {
      chipClass = 'neutral';
    } else if (invertColors) {
      chipClass = isPositive ? 'negative' : 'positive';
    } else {
      chipClass = isPositive ? 'positive' : 'negative';
    }
    const displayValue = formatCurrency(Math.abs(value));
    const sign = showSign && !isZero ? (isPositive ? '+' : '-') : '';
    
    return (
      <span className={`status-chip ${chipClass}`}>
        {sign}{displayValue.replace('$', '').replace('-', '')}
      </span>
    );
  }

  return (
    <div className="fi-table-section">
      <div className="fi-table-header">
        <h3 className="fi-table-title">ISOs</h3>
        <div className="fi-search-container">
          <FontAwesomeIcon icon={['fal', 'search']} className="fi-search-icon" />
          <input
            type="text"
            className="fi-search-input"
            placeholder="Search ISOs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="fi-table-container">
        <table className="fi-table">
          <thead>
            <tr>
              <th className="col-name">ISO Name</th>
              <th className="col-transactions text-right">Transactions</th>
              <th className="col-volume text-right">Volume</th>
              <th className="col-commission text-right">Comm. Owed</th>
              <th className="col-commission text-right">Comm. Paid</th>
              <th className="col-outstanding text-right">Outstanding</th>
              <th className="col-takehome text-right">Take-Home</th>
              <th className="col-actions text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFIs.length === 0 ? (
              <tr>
                <td colSpan={8} className="fi-table-empty">
                  <div className="fi-table-empty-icon">
                    <FontAwesomeIcon icon={['fal', 'store-alt']} />
                  </div>
                  {searchTerm ? 'No ISOs match your search' : 'No ISOs found'}
                </td>
              </tr>
            ) : (
              filteredFIs.map((fi) => {
                const commissionOwed = parseFloat(fi.total_commission_owed || 0);
                const commissionPaid = parseFloat(fi.total_commission_paid || 0);
                const outstanding = commissionOwed - commissionPaid;
                const solventTakeHome = parseFloat(fi.solvent_take_home || 0);
                
                return (
                  <tr key={fi.terra_fi_id}>
                    <td className="col-name">
                      <span
                        className="fi-name-link"
                        onClick={() => handleViewDashboard(fi.terra_fi_id)}
                      >
                        {fi.fi_name || `ISO #${fi.terra_fi_id}`}
                      </span>
                      <div className="fi-id-caption">ID: {fi.terra_fi_id}</div>
                    </td>
                    <td className="col-transactions text-right">
                      {formatNumber(fi.transaction_count)}
                    </td>
                    <td className="col-volume text-right">
                      {formatCurrency(fi.total_volume_usd)}
                    </td>
                    <td className="col-commission text-right">
                      {formatCurrency(commissionOwed)}
                    </td>
                    <td className="col-commission text-right">
                      {formatCurrency(commissionPaid)}
                    </td>
                    <td className="col-outstanding text-right">
                      <StatusChip value={outstanding} />
                    </td>
                    <td className="col-takehome text-right">
                      <StatusChip value={solventTakeHome} showSign />
                    </td>
                    <td className="col-actions text-center">
                      <button
                        className="action-button"
                        onClick={() => handleViewTransactions(fi.terra_fi_id)}
                      >
                        Transactions
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {filteredFIs.length > 0 && (
        <div className="fi-table-footer">
          <span>
            Showing {filteredFIs.length} of {fiMetrics.length} ISOs
          </span>
        </div>
      )}
    </div>
  );
}
